/**
 * Register MCP Server — Scrollable form with 5 sections
 *
 * Sections: Identity, Connection, Docker Sandbox, Credentials, Review
 * Back arrow navigates to /connections.
 * Save validates required fields and navigates back with toast.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ArrowLeft, Server, Shield, ChevronDown,
  Eye, EyeOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
// Codex retest #266 — Badge imported but never rendered in new-mcp.
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useRegisterMcpServer, type RegisterMcpInput } from '@/lib/use-connections-module';
import { useAuth } from '@/contexts/auth-context';
import { ConnectionsRoleGate } from './-connections-role-gate';

export const Route = createFileRoute('/connections/new-mcp')({
  component: NewMcpPage,
});

interface McpFormState {
  name: string;
  description: string;
  namespace: string;
  tier: RegisterMcpInput['tier'];
  endpointUrl: string;
  dockerImage: string;
  dockerTag: string;
  cpuLimit: string;
  memoryLimit: string;
  sandboxed: boolean;
  credentialType: 'token' | 'mtls' | 'none';
  token: string;
  clientCert: string;
  clientKey: string;
}

// Round 4 #228 — also reachable at /connections/mcp/new via the
// nested-IA alias route. Exported so the alias can render this
// component without duplicating wizard logic.
//
// Note: the baseline IA inventory listed `/connections/api/new`,
// `/connections/functions/new`, and `/connections/mcp/marketplace`
// but did not request `/connections/mcp/new` explicitly. Added
// anyway for symmetry with the api/functions sibling routes.
export function NewMcpPage() {
  const navigate = useNavigate();
  // R2 P1-1: this page is the TENANT-scoped Register flow (sysadmin
  // catalog authoring lives at /sysadmin/catalog/mcp/new). Per
  // CLAUDE.md §9.1 Tier 1 = IQ Native, in-process, RLS-enforced —
  // reserved for platform-managed servers under the `iq.*` namespace
  // and provisioned at install time. Tenants cannot self-pick Tier 1,
  // so disable the radio with a tooltip explaining why. The server-
  // side handler at POST /api/v1/connections-module/mcp already rejects
  // tier='tier1' with TIER1_PROVISIONED (connections-module.ts:1084) —
  // this gates the UI to match.
  //
  // A sysadmin who lands on this route (e.g. via a deep link while
  // emulating a tenant context) still sees Tier 1 enabled so they can
  // exercise the full radio for testing, but their POST will land
  // against the tenant handler and be rejected — they should use the
  // sysadmin authoring flow at /sysadmin/catalog/mcp/new instead. We
  // surface that hint in the tooltip text.
  const { isSysadmin } = useAuth();
  const tier1Disabled = !isSysadmin;
  const [form, setForm] = useState<McpFormState>({
    name: '',
    description: '',
    namespace: '',
    tier: 'tier2',
    endpointUrl: '',
    dockerImage: '',
    dockerTag: 'latest',
    cpuLimit: '1',
    memoryLimit: '512MB',
    sandboxed: true,
    credentialType: 'none',
    token: '',
    clientCert: '',
    clientKey: '',
  });
  const [credsOpen, setCredsOpen] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const update = (field: keyof McpFormState, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const canSave = !!(form.name.trim() && form.namespace.trim() && form.endpointUrl.trim());

  const registerMcp = useRegisterMcpServer();

  // Real backend register. Tier 1 is rejected by the backend with
  // 403 TIER1_PROVISIONED (R2 P1-1: bumped from 400). The radio is
  // also disabled for tenant scope so this branch is defensive only.
  const handleSave = () => {
    if (!canSave) return;
    if (form.tier === 'tier1') {
      toast.error(
        'Tier 1 (IQ Native) is platform-managed and cannot be self-registered. Choose Tier 2 (Customer Connected).',
      );
      return;
    }
    registerMcp.mutate(
      {
        slug: form.namespace.trim(),
        displayName: form.name.trim(),
        description: form.description.trim(),
        tier: form.tier,
        endpointUrl: form.endpointUrl.trim(),
        docker: {
          image: form.dockerImage.trim() || form.namespace.trim(),
          tag: form.dockerTag.trim() || 'latest',
          cpuLimit: form.cpuLimit,
          memoryLimit: form.memoryLimit,
          sandboxed: form.sandboxed,
        },
        credentials: {
          type: form.credentialType,
          token: form.credentialType === 'token' ? form.token : undefined,
          clientCert:
            form.credentialType === 'mtls' ? form.clientCert : undefined,
          clientKey: form.credentialType === 'mtls' ? form.clientKey : undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success(`MCP server "${form.name}" registered`);
          navigate({ to: '/connections' });
        },
        onError: (err) => {
          // Surface the backend error message rather than a generic fail toast.
          toast.error(`Register failed: ${err.message}`);
        },
      },
    );
  };

  return (
    <ConnectionsRoleGate backHref="/connections" surface="MCP server">
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate({ to: '/connections' })} aria-label="Back to connections" title="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <div>
            <h1 className="text-lg font-semibold">Register MCP Server</h1>
            <p className="text-xs text-muted-foreground">Configure a new MCP server connection</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/connections' })}>Cancel</Button>
          <Button size="sm" disabled={!canSave || registerMcp.isPending} onClick={handleSave}>
            {registerMcp.isPending ? 'Registering…' : 'Register Server'}
          </Button>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto p-6 max-w-3xl space-y-6">

        {/* Section 1: Identity */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Identity</CardTitle>
            <CardDescription className="text-xs">Name and namespace for this MCP server</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Display Name<span className="text-red-500 ml-0.5">*</span></Label>
              <Input
                placeholder="e.g., Production CMMS"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                maxLength={60}
              />
              <p className="text-[10px] text-muted-foreground text-right">{form.name.length}/60</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                placeholder="Brief description of what this server provides..."
                value={form.description}
                onChange={e => update('description', e.target.value)}
                rows={2}
                maxLength={200}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Namespace<span className="text-red-500 ml-0.5">*</span></Label>
              <Input
                placeholder="e.g., sap"
                value={form.namespace}
                onChange={e => update('namespace', e.target.value)}
                className="font-mono"
              />
              <p className="text-[10px] text-muted-foreground">Lowercase letters, digits, - or _, starting with a letter. Tools will be prefixed: {form.namespace || 'namespace'}.*</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Tier<span className="text-red-500 ml-0.5">*</span></Label>
              <RadioGroup
                value={form.tier}
                onValueChange={v => {
                  // R2 P1-1: defensive — onValueChange still fires for
                  // disabled items in some keyboard-nav paths. Hard-block
                  // tier1 selection from tenants here so the form state
                  // never holds an invalid value.
                  if (v === 'tier1' && tier1Disabled) {
                    toast.error('Tier 1 (IQ Native) is platform-managed and cannot be self-selected by tenant admins.');
                    return;
                  }
                  update('tier', v);
                }}
                className="space-y-2"
              >
                {([
                  { value: 'tier1', label: 'Tier 1 — IQ Native', desc: 'In-process module, highest trust level', icon: Shield },
                  { value: 'tier2', label: 'Tier 2 — Customer Connected', desc: 'Docker-sandboxed, connects to external systems', icon: Server },
                ] as const).map(tier => {
                  const isDisabled = tier.value === 'tier1' && tier1Disabled;
                  // R2 P1-1: explicit per-tier tooltip — visible on hover,
                  // available to AT/screen readers via `title`, surfaces the
                  // reason a control is dimmed (memory `feedback_tooltips_always_visible.md`).
                  const tooltip = isDisabled
                    ? 'Platform-managed only — Tier 1 servers are IQ Native (in-process, RLS-enforced) and provisioned at platform install. Tenant admins cannot self-register Tier 1 connectors.'
                    : tier.desc;
                  const tierTooltip = tooltip;
                  const disabledLabel = 'Platform-managed';
                  const disabledDescription =
                    'Reserved for in-process IQ Native servers (iq.* namespace). Not available to tenant admins.';
                  return (
                    <label
                      key={tier.value}
                      title={tierTooltip}
                      className={cn(
                        'flex items-start gap-3 p-3 rounded-lg border transition-colors',
                        isDisabled
                          ? 'border-border bg-muted/30 opacity-60 cursor-not-allowed'
                          : form.tier === tier.value
                          ? 'border-primary bg-primary/5 cursor-pointer'
                          : 'border-border hover:border-primary/30 cursor-pointer'
                      )}
                      data-tier={tier.value}
                      data-disabled={isDisabled ? 'true' : 'false'}
                    >
                      <RadioGroupItem
                        value={tier.value}
                        disabled={isDisabled}
                        className="mt-0.5"
                        aria-describedby={isDisabled ? `tier-${tier.value}-disabled-reason` : undefined}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <tier.icon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{tier.label}</span>
                          {isDisabled && (
                            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              {disabledLabel}
                            </span>
                          )}
                        </div>
                        <p
                          id={isDisabled ? `tier-${tier.value}-disabled-reason` : undefined}
                          className="text-xs text-muted-foreground mt-0.5"
                        >
                          {isDisabled ? disabledDescription : tier.desc}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Connection */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Connection</CardTitle>
            <CardDescription className="text-xs">How to reach this MCP server</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Endpoint URL<span className="text-red-500 ml-0.5">*</span></Label>
              <Input
                placeholder="e.g., https://sap-mcp.internal:8443"
                value={form.endpointUrl}
                onChange={e => update('endpointUrl', e.target.value)}
                className="font-mono"
              />
              <p className="text-[10px] text-muted-foreground">http(s):// or sse:// MCP endpoints only</p>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Docker Sandbox */}
        {form.tier !== 'tier1' && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Docker Sandbox</CardTitle>
              <CardDescription className="text-xs">Container isolation settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/*
                Fixes #729 (remaining) — UI honesty banner.

                The backend enforces a Docker registry allowlist on
                POST /mcp via the `MCP_REGISTRY_ALLOWLIST` env var
                (see packages/api/src/utils/mcp-registry-allowlist.ts).
                Pre-fix the form happily accepted any string and the
                server silently took it; rejections only surfaced as
                an opaque 400 at submit time. This banner sets the
                expectation BEFORE the user types so they aren't
                surprised when an unknown registry is rejected.

                cosign/Sigstore signature verification and final-IP
                DNS-rebinding checks at pull time are deferred to a
                follow-up — see the PR body.
              */}
              <div
                className="rounded-md border border-amber-300/40 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/40 dark:bg-amber-950/30 dark:text-amber-200"
                role="note"
                aria-label="Docker registry trust policy"
              >
                <strong>Trust policy:</strong> Docker images are validated
                against the platform's registry allowlist before
                installation. If your registry isn't allowed, ask your
                platform sysadmin to add it to{' '}
                <code className="font-mono">MCP_REGISTRY_ALLOWLIST</code>.
                Image signature verification (cosign / Sigstore) is on
                the roadmap.
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Docker Image</Label>
                  <Input
                    placeholder="e.g., ghcr.io/argusai/mcp-sap"
                    value={form.dockerImage}
                    onChange={e => update('dockerImage', e.target.value)}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tag</Label>
                  <Input
                    placeholder="latest"
                    value={form.dockerTag}
                    onChange={e => update('dockerTag', e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">CPU Limit</Label>
                  <Select value={form.cpuLimit} onValueChange={v => update('cpuLimit', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5">0.5 cores</SelectItem>
                      <SelectItem value="1">1 core</SelectItem>
                      <SelectItem value="2">2 cores</SelectItem>
                      <SelectItem value="4">4 cores</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Memory Limit</Label>
                  <Select value={form.memoryLimit} onValueChange={v => update('memoryLimit', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="256MB">256 MB</SelectItem>
                      <SelectItem value="512MB">512 MB</SelectItem>
                      <SelectItem value="1GB">1 GB</SelectItem>
                      <SelectItem value="2GB">2 GB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="text-sm font-medium">Network Sandboxed</p>
                  <p className="text-xs text-muted-foreground">Run with --network=none (recommended for security)</p>
                </div>
                <Switch checked={form.sandboxed} onCheckedChange={v => update('sandboxed', v)} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 4: Credentials (Collapsible) */}
        <Collapsible open={credsOpen} onOpenChange={setCredsOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="pb-3 cursor-pointer hover:bg-muted/30 transition-colors rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm">Credentials</CardTitle>
                    <CardDescription className="text-xs">Authentication for this MCP server</CardDescription>
                  </div>
                  <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', credsOpen && 'rotate-180')} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Credential Type</Label>
                  <Select value={form.credentialType} onValueChange={v => update('credentialType', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No credentials</SelectItem>
                      <SelectItem value="token">Bearer Token</SelectItem>
                      <SelectItem value="mtls">Mutual TLS (mTLS)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.credentialType === 'token' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Token</Label>
                    <div className="relative">
                      <Input
                        type={showToken ? 'text' : 'password'}
                        placeholder="Enter bearer token..."
                        value={form.token}
                        onChange={e => update('token', e.target.value)}
                        className="font-mono pr-10"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                        onClick={() => setShowToken(!showToken)}
                        aria-label={showToken ? 'Hide token' : 'Show token'}
                        title={showToken ? 'Hide' : 'Show'}
                      >
                        {showToken ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                )}

                {form.credentialType === 'mtls' && (
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Client Certificate (PEM)</Label>
                      <Textarea placeholder="-----BEGIN CERTIFICATE-----" value={form.clientCert} onChange={e => update('clientCert', e.target.value)} rows={3} className="font-mono text-xs" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Client Key (PEM)</Label>
                      <Textarea placeholder="-----BEGIN PRIVATE KEY-----" value={form.clientKey} onChange={e => update('clientKey', e.target.value)} rows={3} className="font-mono text-xs" />
                    </div>
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Section 5: Review */}
        {canSave && (
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Review</CardTitle>
              <CardDescription className="text-xs">Verify your configuration before registering</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Name</p>
                  <p className="font-medium">{form.name}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Namespace</p>
                  <p className="font-mono text-xs">{form.namespace}.*</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Tier</p>
                  <p>{form.tier === 'tier1' ? 'Tier 1 — IQ Native' : 'Tier 2 — Connected'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Endpoint</p>
                  <p className="font-mono text-xs truncate">{form.endpointUrl}</p>
                </div>
                {form.tier !== 'tier1' && (
                  <>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Docker</p>
                      <p className="font-mono text-xs">{form.dockerImage || '(not set)'}:{form.dockerTag}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Resources</p>
                      <p>{form.cpuLimit} CPU, {form.memoryLimit}</p>
                    </div>
                  </>
                )}
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Credentials</p>
                  <p>{form.credentialType === 'none' ? 'None' : form.credentialType === 'token' ? 'Bearer Token' : 'mTLS'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Sandboxed</p>
                  <p>{form.sandboxed ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
    </ConnectionsRoleGate>
  );
}
